import { useEffect, useRef } from 'react';
import { AsyncSectionNavigator, pushUrl } from '../../../ips-react-front-fw/navigation';
import { doneTopProgress, failTopProgress, startTopProgress } from '../../../ips-react-front-fw/progress';
import { fetchOrdersSection } from './api';
import type { OrdersMountConfig } from './types';

const ordersContainerSelector = '#orders-container';
const paginationLinkSelector = '#orders-container .woocommerce-pagination a';
const statusFilterSelector = '#order-status-filter';
const dateFilterSelector = '#order-date-filter';
const specificDateSelector = '#order-specific-date';
const orderIdSelector = '#order-id';
const clearOrderIdSelector = '#clear-order-id';
const applyDateSelector = '#ips-apply-date';
const closeDateSelector = '#ips-close-date-popup';
const datePopupSelector = '#specific-date-popup';
const loadingClass = 'ips-orders-pagination-loading';

const toEndpointSlug = (value: string): string => value.replace(/^\/+|\/+$/g, '').trim() || 'orders';

const parsePageFromHref = (href: string, endpointSlug: string): number => {
  try {
    const url = new URL(href, window.location.origin);
    const queryPage = url.searchParams.get('page');
    if (queryPage && /^\d+$/.test(queryPage)) {
      return Math.max(1, Number.parseInt(queryPage, 10));
    }

    const segments = url.pathname.split('/').filter(Boolean);
    for (let i = 0; i < segments.length; i += 1) {
      if (segments[i] !== endpointSlug) {
        continue;
      }

      const maybePage = segments[i + 1];
      if (maybePage && /^\d+$/.test(maybePage)) {
        return Math.max(1, Number.parseInt(maybePage, 10));
      }
    }
  } catch (error) {
    return 1;
  }

  return 1;
};

const readCurrentPage = (endpointSlug: string): number => parsePageFromHref(window.location.href, endpointSlug);

const buildPageHref = (page: number, endpointSlug: string): string => {
  try {
    const url = new URL(window.location.href);
    const pathnameSegments = url.pathname.split('/').filter(Boolean);
    const normalizedPage = String(Math.max(1, page));
    let endpointIndex = -1;

    for (let i = 0; i < pathnameSegments.length; i += 1) {
      if (pathnameSegments[i] === endpointSlug) {
        endpointIndex = i;
        break;
      }
    }

    if (endpointIndex >= 0) {
      const nextSegment = pathnameSegments[endpointIndex + 1];
      if (nextSegment && /^\d+$/.test(nextSegment)) {
        pathnameSegments[endpointIndex + 1] = normalizedPage;
      } else {
        pathnameSegments.splice(endpointIndex + 1, 0, normalizedPage);
      }

      url.pathname = `/${pathnameSegments.join('/')}/`;
      return url.toString();
    }

    url.searchParams.set('page', normalizedPage);
    return url.toString();
  } catch (error) {
    return window.location.href;
  }
};

const getOverlay = (overlayId: string): HTMLElement | null => document.getElementById(overlayId);

const getOrdersContainer = (): HTMLElement | null => document.querySelector<HTMLElement>(ordersContainerSelector);

const getDatePopup = (): HTMLElement | null => document.querySelector<HTMLElement>(datePopupSelector);

const getDateFilter = (): HTMLSelectElement | null => document.querySelector<HTMLSelectElement>(dateFilterSelector);

const readFilterValues = (page: number) => {
  const status = document.querySelector<HTMLSelectElement>(statusFilterSelector)?.value || '';
  const sortOption = getDateFilter()?.value || '';
  const specificDate = document.querySelector<HTMLInputElement>(specificDateSelector)?.value || '';
  const orderId = document.querySelector<HTMLInputElement>(orderIdSelector)?.value || '';

  return {
    status,
    sortOption,
    specificDate,
    orderId,
    page,
  };
};

const replaceOrdersContainerHtml = (container: HTMLElement, html: string): void => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const nextContainer = doc.querySelector<HTMLElement>(ordersContainerSelector);
  container.innerHTML = nextContainer ? nextContainer.innerHTML : html;

  // Keep legacy template behavior where inline scripts inside the injected HTML run.
  const scripts = Array.from(container.querySelectorAll('script'));
  scripts.forEach((oldScript) => {
    const newScript = document.createElement('script');
    for (let i = 0; i < oldScript.attributes.length; i += 1) {
      const attr = oldScript.attributes[i];
      if (attr) {
        newScript.setAttribute(attr.name, attr.value);
      }
    }
    newScript.text = oldScript.textContent || '';
    oldScript.parentNode?.replaceChild(newScript, oldScript);
  });
};

export const useOrdersList = (config: OrdersMountConfig): void => {
  const navigatorRef = useRef<AsyncSectionNavigator | null>(null);
  const hasSpecificDateTriggeredAjaxRef = useRef(false);
  const didEnterSpecificDateViaDropdownRef = useRef(false);
  const lastNonSpecificValueRef = useRef('newest');
  const wasSpecificOpenRef = useRef(false);

  if (!navigatorRef.current) {
    navigatorRef.current = new AsyncSectionNavigator();
  }

  useEffect(() => {
    if (!config.endpoint || !config.nonce) {
      return;
    }

    const endpointSlug = toEndpointSlug(config.endpointSlug);
    const navigator = navigatorRef.current;
    if (!navigator) {
      return;
    }

    const initialDateValue = getDateFilter()?.value || 'newest';
    lastNonSpecificValueRef.current = initialDateValue === 'specific' ? 'newest' : initialDateValue;
    wasSpecificOpenRef.current = initialDateValue === 'specific';

    const isPopupVisible = (): boolean => {
      const popup = getDatePopup();
      if (!popup) {
        return false;
      }

      return window.getComputedStyle(popup).display !== 'none';
    };

    const openSpecificDatePopup = (): void => {
      const popup = getDatePopup();
      const overlay = getOverlay(config.overlayId);

      if (overlay) {
        overlay.style.display = 'block';
      }

      if (popup) {
        popup.style.position = 'fixed';
        popup.style.top = '50%';
        popup.style.left = '50%';
        popup.style.transform = 'translate(-50%, -50%)';
        popup.style.display = 'block';
      }
    };

    const hideSpecificDatePopup = (): void => {
      const popup = getDatePopup();
      const overlay = getOverlay(config.overlayId);

      if (popup) {
        popup.style.display = 'none';
      }

      if (overlay) {
        overlay.style.display = 'none';
      }
    };

    const closeSpecificDatePopup = (): void => {
      const dateFilter = getDateFilter();

      if (
        dateFilter &&
        didEnterSpecificDateViaDropdownRef.current &&
        !hasSpecificDateTriggeredAjaxRef.current &&
        lastNonSpecificValueRef.current
      ) {
        dateFilter.value = lastNonSpecificValueRef.current;
      }

      hasSpecificDateTriggeredAjaxRef.current = false;
      didEnterSpecificDateViaDropdownRef.current = false;
      hideSpecificDatePopup();
    };

    const loadOrders = async (page: number, syncHistory: boolean): Promise<void> => {
      const container = getOrdersContainer();
      if (!container) {
        return;
      }

      const normalizedPage = Math.max(1, page);
      const filters = readFilterValues(normalizedPage);
      const targetHref = buildPageHref(normalizedPage, endpointSlug);

      startTopProgress();
      container.classList.add(loadingClass);
      let hasError = false;

      try {
        const payload = await navigator.run((signal) =>
          fetchOrdersSection(
            {
              endpoint: config.endpoint,
              nonce: config.nonce,
              status: filters.status,
              sortOption: filters.sortOption,
              specificDate: filters.specificDate,
              orderId: filters.orderId,
              page: filters.page,
            },
            signal
          )
        );

        replaceOrdersContainerHtml(container, payload.html);

        if (syncHistory) {
          pushUrl(targetHref, {
            ipsOrdersPagination: true,
            ipsOrdersPage: normalizedPage,
          });
        }

        document.dispatchEvent(new Event('ips:myaccount-content-updated'));
      } catch (error: any) {
        if (error?.name === 'AbortError') {
          return;
        }

        hasError = true;
        if (syncHistory) {
          window.location.href = targetHref;
        }
      } finally {
        if (hasError) {
          failTopProgress();
        } else {
          doneTopProgress();
        }
        container.classList.remove(loadingClass);
      }
    };

    const onChange = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      if (target.matches(statusFilterSelector)) {
        void loadOrders(1, true);
        return;
      }

      if (target.matches(specificDateSelector)) {
        hasSpecificDateTriggeredAjaxRef.current = true;
        void loadOrders(1, true);
        return;
      }

      if (target.matches(dateFilterSelector)) {
        const dateFilter = target as HTMLSelectElement;
        const selected = dateFilter.value;

        if (selected === 'specific') {
          didEnterSpecificDateViaDropdownRef.current = lastNonSpecificValueRef.current !== 'specific';
          openSpecificDatePopup();
          return;
        }

        lastNonSpecificValueRef.current = selected;
        hasSpecificDateTriggeredAjaxRef.current = false;
        didEnterSpecificDateViaDropdownRef.current = false;
        hideSpecificDatePopup();
        void loadOrders(1, true);
      }
    };

    const onClick = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) {
        return;
      }

      const paginationAnchor = target.closest<HTMLAnchorElement>(paginationLinkSelector);
      if (paginationAnchor) {
        event.preventDefault();
        const parsedPage = parsePageFromHref(paginationAnchor.href, endpointSlug);
        const currentPage = readCurrentPage(endpointSlug);
        let nextPage = parsedPage;

        if (!nextPage || nextPage < 1) {
          if (paginationAnchor.classList.contains('woocommerce-button--next')) {
            nextPage = currentPage + 1;
          } else if (paginationAnchor.classList.contains('woocommerce-button--previous')) {
            nextPage = Math.max(1, currentPage - 1);
          } else {
            nextPage = currentPage;
          }
        }

        void loadOrders(nextPage, true);
        return;
      }

      if (target.closest(clearOrderIdSelector)) {
        const orderIdInput = document.querySelector<HTMLInputElement>(orderIdSelector);
        if (orderIdInput) {
          orderIdInput.value = '';
        }
        void loadOrders(1, true);
        return;
      }

      if (target.closest(applyDateSelector)) {
        hideSpecificDatePopup();
        hasSpecificDateTriggeredAjaxRef.current = true;
        void loadOrders(1, true);
        return;
      }

      if (target.closest(closeDateSelector)) {
        closeSpecificDatePopup();
        return;
      }

      const overlay = getOverlay(config.overlayId);
      if (overlay && target === overlay) {
        closeSpecificDatePopup();
        return;
      }

      const dateFilter = target.closest<HTMLSelectElement>(dateFilterSelector);
      if (dateFilter) {
        const selected = dateFilter.value;
        if (selected === 'specific' && wasSpecificOpenRef.current && !isPopupVisible()) {
          openSpecificDatePopup();
        }
        wasSpecificOpenRef.current = selected === 'specific';
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target || !target.matches(orderIdSelector)) {
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        void loadOrders(1, true);
      }
    };

    const onMouseDown = (event: MouseEvent) => {
      if (!isPopupVisible()) {
        return;
      }

      const target = event.target as Node | null;
      const popup = getDatePopup();
      const overlay = getOverlay(config.overlayId);
      if (!target || !popup) {
        return;
      }

      if (popup.contains(target)) {
        return;
      }

      if (overlay && target === overlay) {
        return;
      }

      closeSpecificDatePopup();
    };

    const onPopState = () => {
      void loadOrders(readCurrentPage(endpointSlug), false);
    };

    document.addEventListener('change', onChange, true);
    document.addEventListener('click', onClick, true);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('popstate', onPopState);

    return () => {
      document.removeEventListener('change', onChange, true);
      document.removeEventListener('click', onClick, true);
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onMouseDown, true);
      window.removeEventListener('popstate', onPopState);
      navigator.abort();
    };
  }, [config.endpoint, config.nonce, config.endpointSlug, config.overlayId]);
};
